import { useMemo } from 'react';
import useAuth from './useAuth';

/**
 * Backend V2 resource types for the warehousing module — these are the exact
 * create/update/bulk_upload type strings app.py passes to validate_v2_permission, so a
 * row in user_type_permissions_v2 unlocks the same action here and on the server.
 */
export const WAREHOUSE_RESOURCES = {
	WAREHOUSE: 'warehouse',
	DAILY_MANPOWER: 'warehouse_daily_manpower',
	DAILY_SHIPMENT: 'warehouse_daily_shipment',
	DAILY_BAG: 'warehouse_daily_bag',
	UPDATE_DAILY_COUNT: 'update_warehouse_daily_count',
	DELETE_DAILY_ENTRY: 'delete_warehouse_daily_entry',
	MANPOWER_CSV: 'warehouse_manpower_csv',
	SHIPMENT_CSV: 'warehouse_shipment_csv',
	BAG_CSV: 'warehouse_bag_csv',
	FIXED_COST: 'warehouse_fixed_cost',
	DELETE_FIXED_COST: 'delete_warehouse_fixed_cost',
	MONTHLY_COST_HEAD: 'warehouse_monthly_cost_head',
	MONTHLY_COST_ENTRY: 'warehouse_monthly_cost_entry',
	DELETE_MONTHLY_COST_ENTRY: 'delete_warehouse_monthly_cost_entry',
	REVENUE_ADJUSTMENT: 'warehouse_revenue_adjustment',
	FREEZE: 'warehouse_freeze',
};

// Keyed by the LINE_ITEM_* modes in warehousing.constants.js
const DAILY_CREATE_BY_MODE = {
	manpower: WAREHOUSE_RESOURCES.DAILY_MANPOWER,
	shipment: WAREHOUSE_RESOURCES.DAILY_SHIPMENT,
	bag: WAREHOUSE_RESOURCES.DAILY_BAG,
};

const DAILY_CSV_BY_MODE = {
	manpower: WAREHOUSE_RESOURCES.MANPOWER_CSV,
	shipment: WAREHOUSE_RESOURCES.SHIPMENT_CSV,
	bag: WAREHOUSE_RESOURCES.BAG_CSV,
};

/**
 * The server gates these by role on top of the DB permission row, so the UI checks the
 * same role lists to keep buttons and API agreeing:
 * - freezing Revenue/Cost: warehouse_controller._finance_or_admin_or_406
 * - every Monthly Cost mutation: warehouse_controller._warehouse_cost_editor_or_406
 */
const FINANCE_ADMIN_ROLES = ['finance', 'admin'];
const COST_EDITOR_ROLES = ['finance', 'admin', 'control_tower', 'supervisor'];

// `['all']` is the wildcard the rest of v2 honours (see acl.js)
const hasPermission = (permissions, action, resourceType) => {
	const allowed = permissions?.[action];
	if (!Array.isArray(allowed)) return false;
	if (allowed.includes('all')) return true;
	return allowed.includes(resourceType);
};

/**
 * Action-level ACL for the warehousing module.
 *
 * Warehousing is v2-only, so it reads `v2_permissions` (user_type_permissions_v2) — the
 * V1 `permissions` map never carries warehouse resources.
 *
 * Disallowed actions should be *hidden* rather than rendered disabled, matching how the
 * rest of v2 treats permissions.
 */
export default function useWarehousePermissions() {
	const auth = useAuth();
	const permissions = auth?.v2_permissions;
	const userType = (auth?.user_type || '').toLowerCase();

	return useMemo(() => {
		const isCostEditor = COST_EDITOR_ROLES.includes(userType);

		return {
			canViewWarehousing: hasPermission(permissions, 'search', WAREHOUSE_RESOURCES.WAREHOUSE),

			// Per line item (manpower / shipment / bag) — each is its own create type
			canAddDailyEntry: (mode) =>
				hasPermission(permissions, 'create', DAILY_CREATE_BY_MODE[mode]),
			canUploadDailyCsv: (mode) =>
				hasPermission(permissions, 'bulk_upload', DAILY_CSV_BY_MODE[mode]),
			canEditDailyCount: hasPermission(
				permissions,
				'update',
				WAREHOUSE_RESOURCES.UPDATE_DAILY_COUNT
			),
			canDeleteDailyEntry: hasPermission(
				permissions,
				'update',
				WAREHOUSE_RESOURCES.DELETE_DAILY_ENTRY
			),

			canEditFixedCost: hasPermission(permissions, 'update', WAREHOUSE_RESOURCES.FIXED_COST),
			// Removing a head during an edit is a separate update type
			canDeleteFixedCost: hasPermission(
				permissions,
				'update',
				WAREHOUSE_RESOURCES.DELETE_FIXED_COST
			),

			// Monthly Cost needs both the cost-editor role (handler gate) and the DB
			// permission row (app.py gate) — either alone would show a field that 406s.
			canAddMonthlyCost:
				isCostEditor &&
				hasPermission(permissions, 'create', WAREHOUSE_RESOURCES.MONTHLY_COST_ENTRY),
			// A new head is created and then given its first value, so it needs both
			canAddMonthlyCostHead:
				isCostEditor &&
				hasPermission(permissions, 'create', WAREHOUSE_RESOURCES.MONTHLY_COST_HEAD) &&
				hasPermission(permissions, 'create', WAREHOUSE_RESOURCES.MONTHLY_COST_ENTRY),
			canEditMonthlyCost:
				isCostEditor &&
				hasPermission(permissions, 'update', WAREHOUSE_RESOURCES.MONTHLY_COST_ENTRY),
			canDeleteMonthlyCost:
				isCostEditor &&
				hasPermission(permissions, 'update', WAREHOUSE_RESOURCES.DELETE_MONTHLY_COST_ENTRY),

			// Revenue adjustments ("Additional Revenue Heads") are deliberately NOT in
			// that set — create_warehouse_revenue_adjustment_handler has no role gate —
			// so they stay on the permission table, open to day-to-day ops.
			canAddRevenueAdjustment: hasPermission(
				permissions,
				'create',
				WAREHOUSE_RESOURCES.REVENUE_ADJUSTMENT
			),

			// A freeze is irreversible — there is no unfreeze endpoint — so it stays
			// Finance/Admin-only even though other roles may edit Monthly Cost. The
			// permission row is still required: app.py checks it before the role gate.
			canFreezeWarehouse:
				FINANCE_ADMIN_ROLES.includes(userType) &&
				hasPermission(permissions, 'update', WAREHOUSE_RESOURCES.FREEZE),
		};
	}, [permissions, userType]);
}
